'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { IconButton, Text, Tooltip } from '@terra/ui-design-system';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  SourceCodeIcon,
  GitBranchIcon,
  TerminalIcon,
} from '@terra/ui-icons';
import type { BottomTab, BottomTabType, TabActions } from './types';
import { wheelDeltaYToHorizontalScrollPixels } from '../../../utils/wheelDeltaYToHorizontalScrollPixels';

function getTabIcon(type: BottomTabType) {
  switch (type) {
    case 'terminal':
      return <TerminalIcon size={14} />;
    case 'code-graph':
      return <GitBranchIcon size={14} />;
    case 'decoder':
      return <SourceCodeIcon size={14} />;
    default:
      return <TerminalIcon size={14} />;
  }
}

type BottomTabBarProps = {
  tabs: BottomTab[];
  onTabSelect: (tabId: string) => void;
  onToggleCollapse: () => void;
  isCollapsed: boolean;
  tabActions?: TabActions;
};

export function BottomTabBar({
  tabs,
  onTabSelect,
  onToggleCollapse,
  isCollapsed,
  tabActions,
}: BottomTabBarProps) {
  return (
    <Box
      h="7"
      borderTopWidth="1px"
      borderColor="border.subtle"
      bg="bg.canvas"
      display="flex"
      alignItems="stretch"
    >
      <HStack
        gap={0}
        h="full"
        flex={1}
        overflowY="hidden"
        overflowX="auto"
        css={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
        onWheel={(e) => {
          const el = e.currentTarget;
          const scrollLeftDelta = wheelDeltaYToHorizontalScrollPixels(
            e.nativeEvent,
            el,
          );

          if (scrollLeftDelta !== 0) {
            el.scrollLeft += scrollLeftDelta;
          }
        }}
      >
        {tabs.map((tab) => (
          <Box
            key={tab.id}
            position="relative"
            h="full"
            borderRightWidth="1px"
            borderColor="border.subtle"
            bg={tab.isActive ? 'bg.default' : 'bg.canvas'}
            cursor="pointer"
            minW="100px"
            maxW="180px"
            flex="0 1 auto"
            onClick={() => onTabSelect(tab.id)}
            _hover={{
              bg: tab.isActive ? 'bg.default' : 'bg.subtle',
            }}
          >
            <HStack
              h="full"
              px="3"
              gap="2"
              alignItems="center"
              justifyContent="space-between"
            >
              <HStack gap="2" alignItems="center" flex="1" minW="0">
                <Box color="fg.muted" flexShrink={0}>
                  {getTabIcon(tab.type)}
                </Box>
                <Text
                  fontSize="sm"
                  truncate
                  color={tab.isActive ? 'fg.default' : 'fg.muted'}
                  fontWeight={tab.isActive ? 'medium' : 'normal'}
                >
                  {tab.title}
                </Text>
                {tab.badge !== undefined && tab.badge > 0 && (
                  <Box
                    bg="colorPalette.emphasized"
                    color="colorPalette.fg"
                    borderRadius="full"
                    px="1.5"
                    py="0.5"
                    fontSize="xs"
                    fontWeight="medium"
                    minW="16px"
                    textAlign="center"
                  >
                    {tab.badge}
                  </Box>
                )}
              </HStack>
            </HStack>

            {tab.isActive && (
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                h="1px"
                bg="colorPalette.emphasized"
              />
            )}
          </Box>
        ))}
      </HStack>

      {tabActions && (
        <HStack
          gap="0"
          h="full"
          px="1"
          borderLeftWidth="1px"
          borderColor="border.subtle"
        >
          {tabActions.statusIndicator && (
            <HStack gap="1" px="2" color="fg.muted">
              {tabActions.statusIndicator.icon}
              <Text fontSize="xs">{tabActions.statusIndicator.label}</Text>
            </HStack>
          )}

          {tabActions.customControls?.map((control, index) => (
            <Box key={`custom-control-${index}`} px="1">
              {control}
            </Box>
          ))}

          {tabActions.actions.map((action) => (
            <Tooltip
              key={action.id}
              title={action.label}
              positioning={{ placement: 'top' }}
            >
              <IconButton
                variant="ghost"
                size="xs"
                aria-label={action.label}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </HStack>
      )}

      <Box borderLeftWidth="1px" borderColor="border.subtle">
        <IconButton
          variant="ghost"
          size="sm"
          h="full"
          borderRadius="none"
          aria-label={
            isCollapsed ? 'Expand bottom panel' : 'Collapse bottom panel'
          }
          onClick={onToggleCollapse}
        >
          {isCollapsed ? (
            <ArrowUp01Icon size={16} />
          ) : (
            <ArrowDown01Icon size={16} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
}
